import { Permission, Query, Role } from "appwrite";
import {
	AppwriteClient,
	getAppwriteClient,
	getCurrentUser,
} from "$/modules/appwrite/lib/appwrite";
import { collections } from "$/modules/appwrite/lib/appwrite-collections";
import {
	type Project,
	type ProjectMember,
	ProjectRole,
	ShareMode,
} from "$/modules/appwrite/types/collections";
import { executeExamAction } from "$/modules/exam/lib/exam";

export type MemberRole = ProjectRole;

const isOwner = (project: Project, userId: string) =>
	project.userId.includes(userId);

const normalizeUserId = (userId: string) =>
	userId.replace(/^user:/, "").replace(/\/verified$/, "");

const userRole = (userId: string) => Role.user(normalizeUserId(userId));

const ownerPermissions = (ownerId: string) => [
	Permission.read(userRole(ownerId)),
	Permission.update(userRole(ownerId)),
	Permission.delete(userRole(ownerId)),
];

const sharedReadPermission = (project: Project) => {
	if (project.shareMode === ShareMode.Public)
		return Permission.read(Role.any());
	if (project.shareMode === ShareMode.Members)
		return Permission.read(Role.users());
	if (project.shareMode === ShareMode.Invited && project.shareTeamId) {
		return Permission.read(Role.team(project.shareTeamId));
	}
	return null;
};

export const listProjectCollaborators = async (projectId: string) => {
	const appwrite = getAppwriteClient();
	const members: ProjectMember[] = [];
	let offset = 0;
	while (true) {
		const result = await appwrite.tablesDB.listRows<ProjectMember>(
			AppwriteClient.databaseId,
			collections.projectMembers,
			[
				Query.equal("projectId", projectId),
				Query.limit(100),
				Query.offset(offset),
			],
		);
		members.push(...result.rows);
		if (result.rows.length < 100) return members;
		offset += result.rows.length;
	}
};

export const getProjectRole = async (project: Project, userId: string) => {
	if (isOwner(project, userId)) return ProjectRole.Admin;
	const appwrite = getAppwriteClient();
	const result = await appwrite.tablesDB.listRows<ProjectMember>(
		AppwriteClient.databaseId,
		collections.projectMembers,
		[
			Query.equal("projectId", project.$id),
			Query.equal("userId", userId),
			Query.limit(1),
		],
	);
	return result.rows[0]?.role ?? null;
};

export const requireProjectRole = async (
	project: Project,
	required: "editor" | "admin" = ProjectRole.Editor,
) => {
	const user = await getCurrentUser();
	if (!user) throw new Error("Unauthenticated");
	const role = await getProjectRole(project, user.$id);
	const allowed =
		required === ProjectRole.Editor
			? role === ProjectRole.Editor || role === ProjectRole.Admin
			: role === ProjectRole.Admin;
	if (!allowed) throw new Error("Du hast keine Berechtigung für diese Aktion.");
	return role;
};

export const projectPermissions = (
	project: Project,
	members: ProjectMember[],
) => {
	const permissions = [...ownerPermissions(project.userId[0])];
	const sharedPermission = sharedReadPermission(project);
	if (sharedPermission) permissions.push(sharedPermission);
	for (const member of members) {
		permissions.push(Permission.read(userRole(member.userId)));
		if (
			member.role === ProjectRole.Editor ||
			member.role === ProjectRole.Admin
		) {
			permissions.push(Permission.update(userRole(member.userId)));
		}
		if (member.role === ProjectRole.Admin) {
			permissions.push(Permission.delete(userRole(member.userId)));
		}
	}
	return [...new Set(permissions)];
};

export const contentPermissions = (
	project: Project,
	members: ProjectMember[],
) => {
	const permissions = projectPermissions(project, members).filter(
		(permission) => !permission.startsWith('delete("user:'),
	);
	for (const member of members) {
		if (
			member.role === ProjectRole.Editor ||
			member.role === ProjectRole.Admin
		) {
			permissions.push(Permission.delete(userRole(member.userId)));
		}
	}
	return [...new Set(permissions)];
};

export const syncProjectCollaborationPermissions = async (project: Project) => {
	return executeExamAction<{ success: true }>({
		action: "syncProjectEditorAccess",
		projectId: project.$id,
	});
};

export const inviteProjectMember = async (
	project: Project,
	account: { id: string; email: string; name?: string },
	role: MemberRole,
) => {
	return executeExamAction<{ success: true }>({
		action: "manageProjectMember",
		operation: "upsert",
		projectId: project.$id,
		accountId: account.id,
		role,
	});
};

export const removeProjectMember = async (
	project: Project,
	membershipId: string,
) => {
	const appwrite = getAppwriteClient();
	const membership = await appwrite.tablesDB.getRow<ProjectMember>(
		AppwriteClient.databaseId,
		collections.projectMembers,
		membershipId,
	);
	return executeExamAction<{ success: true }>({
		action: "manageProjectMember",
		operation: "remove",
		projectId: project.$id,
		accountId: membership.userId,
	});
};
